import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Button, Form, Table, Typography } from 'antd';
import { ColumnType } from 'antd/lib/table';
import { keyBy, concat, partition, sum, transform } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import AutoSizer from 'react-virtualized-auto-sizer';
import { useVT } from 'virtualizedtableforantd4';

import { EditableCell } from 'common-ui/cells';
import { EditableListing } from 'common-ui/cells/types';
import { mergeColumnSettings } from 'common-ui/listingConfig';
import { EmptyWrapper, PopConfirmCancelWrapper } from 'common-ui/wrappers';
import {
  AppState,
  ListingConfig,
  listingConfigApi,
  portfolioApi,
  PortfolioListing,
  PortfolioListingUpdate,
} from 'core/lib';
import { OBJECT_TYPES, SYSTEM_ROLES } from 'core/lib/constants';
import { PORTFOLIO_STATUS } from 'core/lib/constants/statuses';
import { selectPortfolioFilteredListingByStatusGroup } from 'core/lib/modules/portfolio/store';
import PermissionControl, { PermissionRules } from 'utils/permissionControl';

import { columns as rawColumns } from './columns';
import ListingHeader from './listingHeader';
import ListingFilters from '../filters/filters';

const { Link } = Typography;

const ACTIONS_WIDTH = 150;

type PortfolioListingProps = {
  listingData?: PortfolioListing[];
};

const Listing = ({ listingData }: PortfolioListingProps) => {
  const { data: listingConfig } = listingConfigApi.useGetListingConfigQuery(OBJECT_TYPES.PORTFOLIO);
  const { t } = useTranslation('portfolio');
  const [form] = Form.useForm();
  const [editingKey, setEditingKey] = useState<number | null>(null);
  const [formDirty, setFormDirty] = useState(false);

  const [updateListingItem, { isLoading: isSaving, isSuccess: isSavingSuccess }] =
    portfolioApi.useUpdateListingItemMutation();

  const listingDataByGroup = useSelector((state: AppState) =>
    selectPortfolioFilteredListingByStatusGroup(state, listingData)
  );

  const isEditing = useCallback((record: PortfolioListing) => record.id === editingKey, [editingKey]);

  const handleValuesChanged = () => {
    setFormDirty(true);
  };

  const edit = (record: PortfolioListingUpdate) => {
    form.setFieldsValue({ ...record });
    setEditingKey(record.id!);
  };

  const cancel = () => {
    setEditingKey(null);
    setFormDirty(false);
  };

  useEffect(() => {
    if (isSavingSuccess) {
      setEditingKey(null);
    }
  }, [isSavingSuccess]);

  const save = async (key: React.Key) => {
    const row: PortfolioListingUpdate = transform(await form.validateFields(), (result, value, key) => {
      result[key] = value ?? null;
    });
    row.id = editingKey!;

    updateListingItem(row);
  };

  const columns = useMemo(() => {
    const orderedColumns = mergeColumnSettings(rawColumns, keyBy(listingConfig, 'dataIndex')).filter(
      (col) => col.visible
    );

    return concat(...partition(orderedColumns, { pinned: true }));
  }, [listingConfig]);

  const mergedColumns = useMemo(
    () =>
      [
        ...columns.map((col) => {
          if (!col.editable || !col.inputType) {
            return col;
          }

          return {
            ...col,
            fixed: col.pinned && 'left',
            onCell: (record: PortfolioListing) => {
              const inputProps = col.getInputProps ? col.getInputProps(record) : col.inputProps;
              return {
                record,
                inputType: col.inputType,
                inputProps: inputProps,
                dataIndex: col.dataIndex,
                title: col.title,
                editing: isEditing(record),
                isSaving: isSaving,
                rules: col.inputRules,
              };
            },
          };
        }),
        {
          title: t('global:general.actions'),
          dataIndex: 'actions',
          width: ACTIONS_WIDTH,
          render: (_: boolean, record: PortfolioListing) => {
            const editable = isEditing(record);
            return editable ? (
              <div className="flex items-center gap-2">
                <Button
                  type="link"
                  disabled={isSaving || !formDirty}
                  loading={isSaving}
                  onClick={() => save(record.id)}
                >
                  {t('actions:global.save')}
                </Button>
                {!isSaving && (
                  <PopConfirmCancelWrapper
                    title={t('actions:confirmation.confirmCancel')}
                    onConfirm={cancel}
                    disabled={!formDirty}
                  >
                    <Link>{t('actions:global.cancel')}</Link>
                  </PopConfirmCancelWrapper>
                )}
              </div>
            ) : (
              <PermissionControl
                rule={PermissionRules.ANY}
                disabled
                roles={[SYSTEM_ROLES.CLIENT_ADMIN, SYSTEM_ROLES.SUPER_ADMIN, SYSTEM_ROLES.SYSTEM_OWNER]}
                render={(_, disabledByRole) => {
                  const editDisabled =
                    editingKey !== null || disabledByRole || record.status === PORTFOLIO_STATUS.COMPLETED;
                  return (
                    <Link disabled={editDisabled} onClick={() => edit(record)}>
                      {t('actions:global.edit')}
                    </Link>
                  );
                }}
              />
            );
          },
        } as ColumnType<PortfolioListing>,
      ] as (ColumnType<PortfolioListing> & ListingConfig & EditableListing<PortfolioListing>)[],
    [columns, isSaving, isEditing, editingKey, t, formDirty] // eslint-disable-line  react-hooks/exhaustive-deps
  );

  const columnsWidth = useMemo(() => sum(columns.map(({ width }) => width).concat(ACTIONS_WIDTH)), [columns]);

  const [vt, set_components, tableRef] = useVT(() => ({ scroll: { x: columnsWidth, y: 500 } }), []);

  useEffect(() => {
    set_components({ body: { cell: EditableCell } });
  }, [set_components]);

  return (
    <>
      <ListingFilters />
      <EmptyWrapper description={t('global:results.emptySearch')} empty={!listingData?.length}>
        <AutoSizer disableWidth className="h-full">
          {({ height }) => (
            <Form
              form={form}
              component={false}
              layout="vertical"
              name="portfolioListingForm"
              onValuesChange={handleValuesChanged}
            >
              <Table
                title={() => <ListingHeader tableRef={tableRef} data={listingData ?? []} />}
                className="h-full ant-table-title-padding"
                rowClassName="ant-design-table-row"
                rowKey="id"
                columns={mergedColumns}
                dataSource={listingDataByGroup}
                pagination={false}
                size="middle"
                scroll={{ y: height - 145, scrollToFirstRowOnChange: true }}
                components={{ ...vt }}
              />
            </Form>
          )}
        </AutoSizer>
      </EmptyWrapper>
    </>
  );
};

export default Listing;
